import { useMemo, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Field, Input, Select } from "../ui";
import { useDocSelector } from "../core/useDocSelector";
import { useWorkspaceDocs } from "../core/useWorkspaceDocs";
import { listTasks } from "../core/tasks";
import { TaskPicker } from "../tasks/TaskPicker";
import { C } from "../core/schema";
import { isHabit as counterIsHabit, type Counter } from "../views/counters/ops";
import type { Action, StepInfo } from "./buttonSteps";

/** the form's first choice: one entry each for a task's state, its timer, and a habit; the choices after the
 *  task or habit decide the action */
type Choice = "taskState" | "taskTimer" | "capture" | "addTask" | "habit" | "counter" | "pomodoro";
type PomodoroOp = "pomodoroStart" | "pomodoroStop" | "pomodoroToggle" | "pomodoroStartPause";
type CheckOp = "habitYes" | "habitNo" | "habitToggle";
type StateOp = "done" | "undone" | "doneToggle";
type TimerOp = "start" | "stop" | "timerToggle" | "timerStartPause";

/**
 * One step of a button: what it does, in which workspace. `children` gets the step once it is complete (else
 * null), so the form can add it to the list or save it with the button. Mounted again (a new `key`) to start over.
 */
export function ButtonStepEditor({
  workspaces,
  home,
  children,
}: {
  /** the workspaces where I can change things */
  workspaces: { id: string; name: string }[];
  /** the button's own workspace: where a step acts unless another one is chosen */
  home: string;
  children(current: StepInfo | null): ReactNode;
}) {
  const { t } = useTranslation();
  const [workspaceId, setWorkspaceId] = useState(home);
  const handles = useWorkspaceDocs([workspaceId]);
  const core = handles[workspaceId];
  const tasksSel = useDocSelector(core, (doc) => listTasks(doc));
  // not archived; done ones too, since a button can set a task back to not done
  const tasks = useMemo(() => (tasksSel ?? []).filter((x) => !x.archivedAt), [tasksSel]);
  const countersJson = useDocSelector(core, (doc) => JSON.stringify(doc.getMap(C.counters).toJSON()));
  // habits and counters share one store; each says which it is (schema v7)
  const allCounters = useMemo(
    () =>
      Object.entries(JSON.parse(countersJson ?? "{}") as Record<string, Counter>)
        .filter(([, c]) => c && typeof c === "object" && !c.archivedAt)
        .map(([id, c]) => ({
          id,
          title: c.title,
          unit: c.unit ?? "",
          kind: c.kind ?? "count",
          habit: counterIsHabit(c),
        }))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [countersJson],
  );
  const [action, setAction] = useState<Choice>("taskState");
  const [stateOp, setStateOp] = useState<StateOp>("done");
  const [timerOp, setTimerOp] = useState<TimerOp>("start");
  const [pomodoroOp, setPomodoroOp] = useState<PomodoroOp>("pomodoroStart");
  const [taskId, setTaskId] = useState("");
  // "selected": whatever task is open in a panel when the button is pressed (else the timer's)
  const [taskMode, setTaskMode] = useState<"specific" | "selected">("specific");
  const [counterId, setCounterId] = useState("");
  const [amount, setAmount] = useState("1");
  const [sign, setSign] = useState<"+" | "-">("+");
  const [checkOp, setCheckOp] = useState<CheckOp>("habitYes");
  const [text, setText] = useState("");

  const picked = tasks.find((x) => x.id === taskId);
  const pickedTitle = picked?.title;
  // a repeating task never stays done: it can only be marked done
  const repeats = taskMode === "specific" && picked?.typeId === "recurring";
  const isHabit = action === "habit";
  // *Interact with a habit* lists the habits, *Interact with a counter* the counters
  const isCounter = action === "counter";
  const habits = allCounters.filter((h) => h.habit);
  const counters = allCounters.filter((h) => !h.habit && h.kind === "count");
  const habit = (isCounter ? counters : habits).find((h) => h.id === counterId);
  const isCount = habit?.kind === "count";
  // what the server is asked for: the habit's kind and the choices after it decide
  const serverAction: Action =
    action === "taskState"
      ? repeats
        ? "done"
        : stateOp
      : action === "taskTimer"
        ? timerOp
        : action === "capture" || action === "addTask"
          ? action
          : action === "pomodoro"
            ? pomodoroOp
            : isCount
              ? sign === "+"
                ? "habitAdd"
                : "habitSubtract"
              : checkOp;
  const n = Number(amount.replace(",", "."));
  const isTask = action === "taskState" || action === "taskTimer";
  const ready =
    action === "capture" || action === "addTask"
      ? text.trim() !== ""
      : action === "pomodoro"
        ? true
        : isHabit || isCounter
          ? !!habit && (!isCount || (n > 0 && n <= 1000))
          : taskMode === "selected" || taskId !== "";
  const current: StepInfo | null = ready
    ? {
        workspaceId,
        workspaceName: workspaces.find((w) => w.id === workspaceId)?.name ?? null,
        action: serverAction,
        taskId: isTask && taskMode === "specific" ? taskId : null,
        taskTitle: isTask && taskMode === "specific" ? (pickedTitle ?? null) : null,
        selected: isTask && taskMode === "selected",
        counterId: isHabit || isCounter ? counterId : null,
        counterTitle: habit?.title ?? null,
        unit: habit?.unit ?? "",
        amount: (isHabit || isCounter) && isCount ? n : null,
        text: action === "capture" || action === "addTask" ? text.trim() : "",
      }
    : null;

  return (
    <div className="flex flex-col gap-3" data-testid="button-step-editor">
      {/* one row: what it does, then the task or habit and what to do with it */}
      {/* the workspace on its own line: what it does, the task and what to do stay on one row */}
      {workspaces.length > 1 && (
        <div className="w-full sm:w-64">
          <Field label={t("buttons.workspace")} htmlFor="button-workspace">
            <Select
              id="button-workspace"
              value={workspaceId}
              onChange={(e) => {
                setWorkspaceId(e.target.value);
                setTaskId("");
                setCounterId("");
              }}
              data-testid="button-workspace"
            >
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      )}
      <div className="flex flex-wrap items-start gap-3" data-testid="buttons-what">
        <div className="w-full shrink-0 sm:w-64">
          <Field label={t("buttons.action")} htmlFor="button-action">
            <Select
              id="button-action"
              value={action}
              onChange={(e) => {
                setAction(e.target.value as Choice);
                setCounterId("");
              }}
              data-testid="button-action"
            >
              <option value="taskState">{t("buttons.actions.taskState")}</option>
              <option value="taskTimer">{t("buttons.actions.taskTimer")}</option>
              <option value="addTask">{t("buttons.actions.addTask")}</option>
              <option value="capture">{t("buttons.actions.capture")}</option>
              <option value="habit">{t("buttons.actions.habit")}</option>
              <option value="counter">{t("buttons.actions.counter")}</option>
              <option value="pomodoro">{t("buttons.actions.pomodoro")}</option>
            </Select>
          </Field>
        </div>
        {isCounter ? (
          <div className="min-w-[16rem] flex-1">
            <Field label={t("buttons.counter")} htmlFor="button-counter">
              {counters.length === 0 ? (
                <p className="text-[12.5px] text-neutral-500" data-testid="button-no-counter">
                  {t("buttons.noCounter")}
                </p>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    id="button-counter"
                    value={counterId}
                    onChange={(e) => setCounterId(e.target.value)}
                    data-testid="button-counter"
                    className="min-w-0 flex-1"
                  >
                    <option value="">{t("buttons.pickCounter")}</option>
                    {counters.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.title}
                      </option>
                    ))}
                  </Select>
                  <Select
                    value={sign}
                    onChange={(e) => setSign(e.target.value as "+" | "-")}
                    aria-label={t("buttons.sign")}
                    data-testid="button-sign"
                    className="w-16"
                  >
                    <option value="+">+</option>
                    <option value="-">−</option>
                  </Select>
                  <Input
                    className="w-20"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    aria-label={t("buttons.amount")}
                    data-testid="button-amount"
                  />
                  {habit?.unit && <span className="text-[12.5px] text-neutral-500">{habit.unit}</span>}
                </div>
              )}
            </Field>
          </div>
        ) : action === "pomodoro" ? (
          <div className="min-w-[16rem] flex-1">
            <Field label={t("buttons.pomodoroOp")} htmlFor="button-pomodoro-op">
              <Select
                id="button-pomodoro-op"
                value={pomodoroOp}
                onChange={(e) => setPomodoroOp(e.target.value as PomodoroOp)}
                data-testid="button-pomodoro-op"
                className="w-fit"
              >
                <option value="pomodoroStart">{t("buttons.pomodoroOps.pomodoroStart")}</option>
                <option value="pomodoroStop">{t("buttons.pomodoroOps.pomodoroStop")}</option>
                <option value="pomodoroToggle">{t("buttons.pomodoroOps.pomodoroToggle")}</option>
                <option value="pomodoroStartPause">{t("buttons.pomodoroOps.pomodoroStartPause")}</option>
              </Select>
            </Field>
          </div>
        ) : isHabit ? (
          <div className="min-w-[16rem] flex-1">
            <Field label={t("buttons.habit")} htmlFor="button-habit">
              {habits.length === 0 ? (
                <p className="text-[12.5px] text-neutral-500" data-testid="button-no-habit">
                  {t("buttons.noHabit")}
                </p>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    id="button-habit"
                    value={counterId}
                    onChange={(e) => setCounterId(e.target.value)}
                    data-testid="button-habit"
                    className="min-w-0 flex-1"
                  >
                    <option value="">{t("buttons.pickHabit")}</option>
                    {habits.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.title}
                      </option>
                    ))}
                  </Select>
                  {habit && isCount && (
                    <>
                      <span className="text-[13px] text-neutral-600 dark:text-neutral-300">
                        {t("buttons.count")}
                      </span>
                      <Select
                        value={sign}
                        onChange={(e) => setSign(e.target.value as "+" | "-")}
                        aria-label={t("buttons.sign")}
                        data-testid="button-sign"
                        className="w-16"
                      >
                        <option value="+">+</option>
                        <option value="-">−</option>
                      </Select>
                      <Input
                        className="w-20"
                        inputMode="decimal"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        aria-label={t("buttons.amount")}
                        data-testid="button-amount"
                      />
                      {habit.unit && <span className="text-[12.5px] text-neutral-500">{habit.unit}</span>}
                    </>
                  )}
                  {habit && !isCount && (
                    <Select
                      value={checkOp}
                      onChange={(e) => setCheckOp(e.target.value as CheckOp)}
                      aria-label={t("buttons.checkOp")}
                      data-testid="button-check-op"
                    >
                      <option value="habitYes">{t("buttons.checkOps.habitYes")}</option>
                      <option value="habitNo">{t("buttons.checkOps.habitNo")}</option>
                      <option value="habitToggle">{t("buttons.checkOps.habitToggle")}</option>
                    </Select>
                  )}
                </div>
              )}
            </Field>
          </div>
        ) : action === "capture" || action === "addTask" ? (
          <div className="min-w-[16rem] flex-1">
            <Field
              label={t(action === "capture" ? "buttons.text" : "buttons.taskLine")}
              htmlFor="button-text"
            >
              <Input
                id="button-text"
                value={text}
                placeholder={action === "addTask" ? t("buttons.taskLinePlaceholder") : undefined}
                onChange={(e) => setText(e.target.value)}
                data-testid="button-text"
              />
            </Field>
          </div>
        ) : (
          <div className="min-w-[16rem] flex-1">
            <Field label={t("buttons.task")}>
              {/* one line: the task, then what to do with it */}
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={taskMode}
                  onChange={(e) => setTaskMode(e.target.value as "specific" | "selected")}
                  aria-label={t("buttons.taskMode")}
                  data-testid="button-task-mode"
                  className="w-fit"
                >
                  <option value="specific">{t("buttons.taskModes.specific")}</option>
                  <option value="selected">{t("buttons.taskModes.selected")}</option>
                </Select>
                {taskMode === "selected" ? null : pickedTitle ? (
                  <>
                    <span className="min-w-0 truncate text-sm" data-testid="button-picked-task">
                      {pickedTitle}
                    </span>
                    <button
                      type="button"
                      aria-label={t("buttons.otherTask")}
                      title={t("buttons.otherTask")}
                      onClick={() => setTaskId("")}
                      className="grid h-6 w-6 shrink-0 place-items-center rounded-sm text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 touch:h-11 touch:w-11 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
                      data-testid="button-task-clear"
                    >
                      <X size={13} aria-hidden />
                    </button>
                  </>
                ) : (
                  <div className="min-w-[7rem] flex-1">
                    <TaskPicker
                      tasks={tasks}
                      exclude={new Set()}
                      onPick={setTaskId}
                      placeholder={t("buttons.pickTask")}
                      testId="button-task"
                    />
                  </div>
                )}
                {action === "taskState" ? (
                  <Select
                    value={repeats ? "done" : stateOp}
                    onChange={(e) => setStateOp(e.target.value as StateOp)}
                    aria-label={t("buttons.stateOp")}
                    data-testid="button-state-op"
                    className="ml-auto"
                  >
                    <option value="done">{t("buttons.stateOps.done")}</option>
                    {!repeats && <option value="undone">{t("buttons.stateOps.undone")}</option>}
                    {!repeats && <option value="doneToggle">{t("buttons.stateOps.doneToggle")}</option>}
                  </Select>
                ) : (
                  <Select
                    value={timerOp}
                    onChange={(e) => setTimerOp(e.target.value as TimerOp)}
                    aria-label={t("buttons.timerOp")}
                    data-testid="button-timer-op"
                    className="ml-auto"
                  >
                    <option value="start">{t("buttons.timerOps.start")}</option>
                    <option value="stop">{t("buttons.timerOps.stop")}</option>
                    <option value="timerToggle">{t("buttons.timerOps.timerToggle")}</option>
                    <option value="timerStartPause">{t("buttons.timerOps.timerStartPause")}</option>
                  </Select>
                )}
              </div>
              {taskMode === "selected" && (
                <p className="text-[12px] text-neutral-500">{t("buttons.selectedHint")}</p>
              )}
              {action === "taskState" && repeats && (
                <p className="text-[12px] text-neutral-500">{t("buttons.repeatsOnlyDone")}</p>
              )}
            </Field>
          </div>
        )}
      </div>
      {action === "addTask" && <p className="text-[12px] text-neutral-500">{t("buttons.taskLineHint")}</p>}
      {children(current)}
    </div>
  );
}
